import { StyleDeclarationValue, css } from 'aphrodite';
import { observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { openDownloadUrl } from '../../../../../models/UiUtils';
import { useErrorMessages } from '../../../../../models/hooks/appStateHooks';
import { LoadingSpinner } from '../../../../../web/components/LoadingSpinner';
import { SuccessCheckIcon } from '../../../../../web/components/svgs/icons/SuccessCheckIcon';
import { baseStyleSheet } from '../../../../../web/styles/styles';
import {
	IFileSourceJobState,
	IIntegrationFileSource,
	IIntegrationJobState,
	IIntegrationJobStateWithLoadCounts,
	IntegrationJobType,
} from '../../../../models';
import { AdminConfigurableIntegrationViewModel } from '../../../../viewModels';
import { styleSheet } from './styles';

interface IConfigurableIntegrationSettingsAdminRecentActivityProps {
	integrationSettings: AdminConfigurableIntegrationViewModel;
	styles?: StyleDeclarationValue[];
}
const dateFormatPattern = 'MMM D, h:mmA';

export const ConfigurableIntegrationSettingsAdminRecentActivity: React.FC<IConfigurableIntegrationSettingsAdminRecentActivityProps> =
	observer(({ integrationSettings, styles = [] }) => {
		const errorMessages = useErrorMessages();
		const [loading, setLoading] = React.useState<boolean>(true);

		React.useEffect(() => {
			integrationSettings
				?.loadRecentActivity()
				?.then(() => {
					setLoading(false);
				})
				?.catch(error => {
					errorMessages.pushApiError(error);
					setLoading(false);
				});
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []);

		const onDownloadLinkClicked = async (
			index: number,
			fileSources: IIntegrationFileSource[],
			integrationJobState: IIntegrationJobState
		) => {
			try {
				const pathComps = fileSources[index].path.split('/');
				const value = await integrationSettings?.getIntegrationJobStateFileSourceDownloadUrl(
					integrationJobState as IFileSourceJobState,
					index
				);

				openDownloadUrl(value, pathComps[pathComps.length - 1]);
			} catch (error) {
				errorMessages.pushApiError(error);
			}
		};

		return (
			<div
				className={`configurable-integration-settings-recent-activity ${css(
					styleSheet.recentActivityCards,
					...styles
				)}`}
			>
				{loading ? (
					<LoadingSpinner type='small' />
				) : integrationSettings?.recentActivity?.length > 0 ? (
					<div className={css(styleSheet.tableContainer)}>
						<table className={css(styleSheet.container)}>
							<thead className={css(styleSheet.tableHeader)}>
								<tr>
									<th className={css(styleSheet.tableHeaderCell, styleSheet.tableHeaderInitialsHeader)}>
										Job Start Time
									</th>
									<th className={css(styleSheet.tableHeaderCell, styleSheet.tableHeaderInitialsHeader)}>End Time</th>
									<th colSpan={2} className={css(styleSheet.tableHeaderCell, styleSheet.tableHeaderInitialsHeader)}>
										Description
									</th>
									<th className={css(styleSheet.tableHeaderCell, styleSheet.tableHeaderInitialsHeader)}>Job Type</th>
									<th className={css(styleSheet.tableHeaderCell, styleSheet.tableHeaderInitialsHeader)}>
										Failure Count
									</th>
									<th className={css(styleSheet.tableHeaderCell, styleSheet.tableHeaderInitialsHeader)}>
										Current Extract Count
									</th>

									<th className={css(styleSheet.tableHeaderCell, styleSheet.tableHeaderInitialsHeader)}>
										Total Load Count
									</th>

									<th className={css(styleSheet.tableHeaderCell, styleSheet.tableHeaderInitialsHeader)}>
										Current Load Count
									</th>

									<th className={css(styleSheet.tableHeaderCell, styleSheet.tableHeaderInitialsHeader)}>Actions</th>
								</tr>
							</thead>

							<tbody>
								{integrationSettings?.recentActivity?.map((integrationJobState, index) => {
									const fileSources = (integrationJobState as IFileSourceJobState)?.fileSources;
									const isLoadOrReloadJob = Object.prototype.hasOwnProperty.call(
										integrationJobState as IIntegrationJobStateWithLoadCounts,
										'totalToLoadCount'
									);

									return (
										<tr key={`test-${index}`}>
											<td className={css(styleSheet.cell)}>
												{moment(integrationJobState.jobStartedAt).format(dateFormatPattern)}
											</td>
											<td className={css(styleSheet.cell)}>
												{integrationJobState.jobEndedAt ? (
													<span className={css(baseStyleSheet.horizontalStack)}>
														<SuccessCheckIcon />
														<span>{moment(integrationJobState.jobEndedAt).format(dateFormatPattern)}</span>
													</span>
												) : (
													'In Progress'
												)}
											</td>
											{integrationJobState.jobDescription && (
												<td className={css(styleSheet.cell)} colSpan={2}>
													<div>
														<div className={css(styleSheet.description)}>{integrationJobState.jobDescription}</div>
													</div>
												</td>
											)}
											<td className={css(styleSheet.cell)}>{integrationJobState.jobType}</td>
											<td className={css(styleSheet.cell)}>{integrationJobState.failureCount}</td>
											<td className={css(styleSheet.cell)}>
												{integrationJobState.jobType === IntegrationJobType.Extract && (
													<div>
														<div>{integrationJobState.currentExtractCount}</div>
													</div>
												)}
											</td>

											{isLoadOrReloadJob && (
												<td className={css(styleSheet.cell)}>
													<div>{(integrationJobState as IIntegrationJobStateWithLoadCounts).totalToLoadCount}</div>
												</td>
											)}

											{isLoadOrReloadJob && (
												<td className={css(styleSheet.cell)}>
													<div>{(integrationJobState as IIntegrationJobStateWithLoadCounts).currentLoadCount}</div>
												</td>
											)}

											<td className={css(styleSheet.cell)}>
												{fileSources?.length > 0 && (
													<div>
														<div className={css(baseStyleSheet.verticalStack)}>
															{fileSources.map((x, i) => {
																const pathComps = x.path.split('/');
																return (
																	<div
																		className={css(styleSheet.description, styleSheet.fileSourcePath)}
																		key={`${x.path}-${i}`}
																	>
																		<div>
																			<button
																				className={css(baseStyleSheet.brandLink)}
																				onClick={() => onDownloadLinkClicked(i, fileSources, integrationJobState)}
																			>
																				<span>{pathComps[pathComps.length - 1]}</span>
																			</button>
																		</div>
																	</div>
																);
															})}
														</div>
													</div>
												)}
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				) : (
					<span>No activity</span>
				)}
			</div>
		);
	});
